import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { PenSquare, ShieldCheck, ServerCog } from 'lucide-react';
import { InstanceSetupSchema } from '@blogforge/shared';
import type { InstanceSetupInput } from '@blogforge/shared';
import { api, ApiError } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';

// ─────────────────────────────────────────────────────────────────
// First-run instance setup (/setup)
//
// Private beta has no signup form, so a fresh deployment needs one
// deliberate way to create the account that creates every other account.
// This page claims the instance: it creates the first platform admin, signs
// them in and drops them into their new workspace — which immediately sends
// them through the workspace onboarding wizard.
//
// The server closes this the moment a platform admin exists (the claim is a
// single atomic INSERT ... WHERE NOT EXISTS), so the page also redirects to
// /login when it finds the instance already claimed: an unauthenticated
// account-creation screen must never be reachable on a live instance.
// ─────────────────────────────────────────────────────────────────

/** Setup form = the API payload plus a confirmation field. */
const SetupFormSchema = InstanceSetupSchema.extend({
  confirmPassword: z.string().min(1, 'Confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type SetupFormInput = z.infer<typeof SetupFormSchema>;

export function InstanceSetupPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [serverError, setServerError] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ['instance-setup-status'],
    queryFn: () => api.setup.status(),
    staleTime: 0,
    retry: false,
  });

  // Already claimed (seeded, or set up by someone else): there is nothing to
  // do here — send them to sign in.
  useEffect(() => {
    if (status.data && !status.data.needsSetup) {
      navigate('/login', { replace: true });
    }
  }, [status.data, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SetupFormInput>({
    resolver: zodResolver(SetupFormSchema),
  });

  const onSubmit = async (data: SetupFormInput) => {
    setServerError(null);
    try {
      const payload: InstanceSetupInput = {
        email: data.email,
        name: data.name,
        password: data.password,
        ...(data.workspaceName ? { workspaceName: data.workspaceName } : {}),
      };
      const res = await api.setup.create(payload);
      // The claim signs us in (cookie + refresh token), so go straight in.
      login(res.user, res.refreshToken);
      navigate(res.nextUrl || '/posts', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err.message);
        // 409 means someone claimed it between page load and submit.
        if (err.status === 409) {
          setTimeout(() => navigate('/login', { replace: true }), 1500);
        }
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    }
  };

  // A claimed instance navigates away; render nothing rather than a form that
  // would 409 on submit.
  if (status.data && !status.data.needsSetup) return null;

  // A closed one gets an explanation rather than a form that would 403.
  if (status.data && !status.data.setupAllowed) {
    return (
      <div className="bg-bg text-ink flex min-h-screen items-center justify-center px-6 py-12">
        <div className="glass-card w-full max-w-[460px] p-8 text-center">
          <p className="text-[15px] font-semibold">Setup is disabled on this instance</p>
          <p className="text-ink-soft text-body-sm mt-2">
            This deployment has no administrator account yet, but first-run setup has been switched
            off by the operator (<code className="font-mono text-[12px]">SETUP_ENABLED=false</code>
            ). The operator can enable it, or create the account directly in the database.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="btn-ghost mt-5 px-3 py-2 text-[12.5px]"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bg text-ink flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-[520px]">
        <header className="flex items-center justify-between gap-4">
          <span className="bg-accent text-accent-ink rounded-btn flex h-7 w-7 items-center justify-center">
            <PenSquare size={15} strokeWidth={2} />
          </span>
          <span className="text-ink-soft font-mono text-[11px] tracking-[0.04em] uppercase">
            First run
          </span>
        </header>

        <div className="glass-card mt-8 p-8">
          <p className="eyebrow">
            <ServerCog size={12} /> Set up this instance
          </p>
          <h1 className="text-heading-sm mt-4 font-semibold">Create the administrator account</h1>
          <p className="text-ink-soft text-body-sm mt-2">
            This is a one-time step. The account you create here can add every other account &mdash;
            BlogForger runs in private beta with no public sign-up.
          </p>

          {serverError && (
            <div
              role="alert"
              className="border-danger/40 text-danger text-body-sm rounded-btn mt-5 border px-3.5 py-3"
            >
              {serverError}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-5">
            <div>
              <label htmlFor="name" className="field-label">
                Your name
              </label>
              <input
                id="name"
                {...register('name')}
                className="field-input"
                placeholder="Ada Lovelace"
              />
              {errors.name && (
                <p className="text-danger mt-1.5 text-[12px]">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="field-label">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email')}
                className="field-input"
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="text-danger mt-1.5 text-[12px]">{errors.email.message}</p>
              )}
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="password" className="field-label">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  {...register('password')}
                  className="field-input"
                  placeholder="At least 8 characters"
                />
                {errors.password && (
                  <p className="text-danger mt-1.5 text-[12px]">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="field-label">
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  {...register('confirmPassword')}
                  className="field-input"
                  placeholder="Repeat it"
                />
                {errors.confirmPassword && (
                  <p className="text-danger mt-1.5 text-[12px]">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="workspaceName" className="field-label">
                Workspace name <span className="text-muted font-normal">(optional)</span>
              </label>
              <input
                id="workspaceName"
                {...register('workspaceName')}
                className="field-input"
                placeholder="Acme Publishing"
                maxLength={80}
              />
              <p className="text-muted mt-1 px-0.5 text-[10.5px]">
                Your first workspace. You can rename it, or create more, later.
              </p>
            </div>

            <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
              {isSubmitting ? (
                <>
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Setting up…
                </>
              ) : (
                'Create administrator & continue'
              )}
            </button>
          </form>

          <div className="border-line mt-6 flex items-center gap-2 border-t pt-4">
            <ShieldCheck size={13} className="text-ink-soft shrink-0" />
            <p className="text-ink-soft text-[11.5px]">
              This page closes itself once an administrator exists.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
